import { API_URL } from "../config/config"
import { getRequest, patchRequest, postRequest } from "../utils/axiosUtils"
import { getDateFormat, getFilterQuery, getStatusLabel } from "../utils/helperFunction"

export const fetchSpecializationsNames = async (setSpecializations) => {
    try {
        const response = await getRequest({
            url: `${API_URL}/specialization/name`,
        })

        if (response.status >= 200 && response.status < 300 && response.data) {
            const options = response.data?.specializations?.map(spec => ({
                value: spec._id,
                label: spec.specializationName
            }))
            setSpecializations(options)
        } else {
            setSpecializations([])
            throw new Error(`Unexpected response code: ${response.status}`)
        }
    } catch (error) {
        setSpecializations([])
        console.error(error)
    }
}

// export const saveSpecialization = async (specializationData) => {
//     if (!specializationData) {
//         throw new Error('Specialization data is required.')
//     }

//     const payload = {}
//     specializationData.forEach(field => {
//         if (
//             field.type === 'select' &&
//             typeof field.value === 'object' &&
//             field.value !== null
//         ) {
//             payload[field.id] = field.value.value
//         } else {
//             payload[field.id] = field.value
//         }
//     })

//     try {
//         const response = await postRequest({
//             url: `${API_URL}/specialization/save`,
//             data: payload,
//             headers: {
//                 'Content-Type': 'application/json'
//             },
//         })

//         if (response?.data?.success === false) {
//             const customError = new Error(response.data.message || 'Validation failed')
//             customError.fieldErrors = response.data.errors || {}
//             throw customError
//         }

//         return response.data
//     } catch (err) {
//         const responseData = err?.response?.data
//         if (responseData) {
//             const customError = new Error(responseData.message || 'Specialization saving failed')
//             customError.fieldErrors = responseData.errors || {}
//             throw customError
//         }

//         throw new Error(err.message || 'Unknown error occurred')
//     }
// }

export const saveSpecialization = async (specializationData) => {
    if (!specializationData) {
        throw new Error('Specialization data is required.')
    }

    const payload = {}

    specializationData.forEach(field => {
        const idOnlyFields = ['topRecruitersId', 'toolsTechnologiesUsedId'];

        if (idOnlyFields.includes(field.id) && Array.isArray(field.value)) {
            payload[field.id] = field.value.map(r =>
                typeof r === 'object'
                    ? (r._id || r.value)
                    : r
            );
        }
        else if (field.id === 'courseId' && typeof field.value === 'object' && field.value !== null) {
            payload.courseNameId = field.value.value;
            payload.courseId = field.value.courseId;
        }
        else if (
            field.type === 'select' &&
            typeof field.value === 'object' &&
            field.value !== null
        ) {
            payload[field.id] = field.value.value
        } else {
            payload[field.id] = field.value
        }
    })

    try {
        const response = await postRequest({
            url: `${API_URL}/specialization/save`,
            data: payload,
            headers: {
                'Content-Type': 'application/json'
            },
        })

        if (response?.data?.success === false) {
            const customError = new Error(response.data.message || 'Validation failed')
            customError.fieldErrors = response.data.errors || {}
            throw customError
        }

        return response.data
    } catch (err) {
        const responseData = err?.response?.data
        if (responseData) {
            const customError = new Error(responseData.message || 'Specialization saving failed')
            customError.fieldErrors = responseData.errors || {}
            throw customError
        }

        throw new Error(err.message || 'Unknown error occurred')
    }
}

export const fetchSpecialization = async (specializationId, fieldTemplate = [],editMode) => {
    if (!specializationId) {
        throw new Error('Specialization ID is required')
    }

    try {
        const response = await getRequest({
            url: `${API_URL}/specialization/${specializationId}`,
        })

        const specialization = response?.data?.specialization

        if (!response?.data?.success || !specialization) {
            const error = new Error(response?.data?.message || 'Specialization not found')
            error.fieldErrors = response?.data?.errors || {}
            throw error
        }
        const isDraft = editMode || ['draft', 'rework'].includes(specialization.status);

        const populatedFields = fieldTemplate.map(field => {
            const rawValue = specialization[field.id]

            if (field.id === 'uploadRelevantDocument' && Array.isArray(rawValue)) {
                const documents = rawValue.map(doc => ({
                    title: doc.title || '',
                    file: null,
                    uploading: false,
                    url: doc.url || ''
                }))
                return {
                    ...field,
                    value: documents,
                    readOnly: !isDraft
                }
            }
            if (['toolsTechnologiesUsedId','topRecruitersId']?.includes(field.id) && Array.isArray(rawValue)) {
                const documents = rawValue.map(doc => ({
                   ...doc,
                   value:doc?._id
                }))
                return {
                    ...field,
                    value: documents,
                    readOnly: !isDraft
                }
            }
            if (field.type === 'select' && rawValue) {
                if (field.id === 'courseId') {
                    return {
                        ...field,
                        value: {
                            label: specialization['courseNameId']?.name || '',
                            value: specialization['courseNameId']?._id || '',
                            courseId: specialization['courseId']
                        },
                        readOnly: !isDraft
                    }
                }
                if (typeof rawValue === 'object') {
                    if (field.id === 'universityId' && (rawValue.universityName || rawValue.name)) {
                        return {
                            ...field,
                            value: {
                                label: rawValue.universityName || rawValue.name || '',
                                value: rawValue._id || ''
                            },
                            readOnly: !isDraft
                        }
                    }

                    // Fallback for other select fields
                    return {
                        ...field,
                        value: {
                            label: rawValue.label || rawValue.name || rawValue.value || '',
                            value: rawValue._id || rawValue.value || '',
                        },
                        readOnly: !isDraft
                    }
                } else {
                    return {
                        ...field,
                        value: {
                            label: String(rawValue),
                            value: rawValue
                        },
                        readOnly: !isDraft
                    }
                }
            }

            return {
                ...field,
                value: rawValue ? rawValue : (["applicationPeriod","minimumPercentageCGPA","workExperienceRequired"]?.includes(field?.id) ? {} :''),
                readOnly: !isDraft
            }
        })

        return populatedFields

    } catch (err) {
        const responseData = err?.response?.data
        if (responseData) {
            const customError = new Error(responseData.message || 'Specialization fetch failed')
            customError.fieldErrors = responseData.errors || {}
            throw customError
        }
        throw new Error(err.message || 'Unknown error occurred while fetching specialization')
    }
}

export const updateSpecializationStatus = async (id, specializationStatus, reason = '') => {
    if (!id || !specializationStatus) {
        throw new Error('Specialization ID and status are required')
    }

    const response = await patchRequest({
        url: `${API_URL}/specialization/status/${id}`,
        data: { status: specializationStatus, reason },
        headers: {
            'Content-Type': 'application/json',
        },
    })

    if (response?.data?.success === false) {
        throw new Error(response.data.message || 'Status update failed')
    }

    return response.data
}

export const fetchSpecializationList = async (setRows, filterData = {}, userId = true,setRowCount) => {
    try {
    const query = getFilterQuery(filterData)
        const url = userId
            ? `${API_URL}/specialization/user${query}`
            : `${API_URL}/specialization/all${query}`

        const response = await getRequest({
            url,
            // data: filterData,
        })

        if (response.status >= 200 && response.status < 300 && response.data) {
              const pagination = response.data?.pagination
            const options = (response.data.specializations || []).map((spec, idx) => ({
                sNo: (pagination?.page-1)*pagination.limit + (idx+1),
                id: spec._id,
                specId: spec.specialisationId || '',
                name: spec.specialization || '',
                courseName: spec.course || '',
                universityId: spec.universityId || '',
                university: spec.universityName || '',
                universityLogo: spec.universityLogo || '',
                status: (spec.status || '').charAt(0).toUpperCase() + (spec.status || '').slice(1),
                completion: spec.completionPercentage,
                createdDate: spec.createdAt ? new Date(spec.createdAt).toLocaleDateString() : '',
                lastUpdate: spec.updatedAt ? new Date(spec.updatedAt).toLocaleDateString() : '',
                createdBy: spec.createdBy || '',
                reviewedBy: spec.reviewedBy || '',
                comments: Array.isArray(spec.notes) && spec.notes.length > 0
                    ? spec.notes.join(', ')
                    : '',
            }))
            setRows(options)
            setRowCount && setRowCount(response.data?.pagination?.total || 0)
        } else {
            setRows([])
            throw new Error(`Unexpected response code: ${response.status}`)
        }
    } catch (error) {
        setRows([])
        console.error(error)
    }
}

export const fetchSpecializationsByNameId = async (nameId, setRows, filterData = {},setRowCount) => {
    if (!nameId) {
        setRows?.([]);
        throw new Error('specialization nameId is required');
    }
    let rowCount = 0
    try {
        const query = getFilterQuery(filterData)
        const response = await getRequest({
            url: `${API_URL}/specialization/name/${nameId}${query}`,
            data: filterData,
        });

        if (response.status >= 200 && response.status < 300 && response.data) {
             const pagination = response.data?.pagination
            const list = Array.isArray(response.data.specializations)
                ? response.data.specializations
                : [];

            const rows = list.map((spec,idx) => ({
                 sNo: (pagination?.page-1)*pagination.limit + (idx+1),
                id: spec?._id,
                specId: spec?.specialisationId || spec?.specializationId || '',
                name:
                    spec?.specializationNameId?.name ||
                    spec?.specialization ||
                    '',
                courseId: spec?.courseId?._id || spec?.courseId || '',
                courseName:
                    spec?.courseNameId?.name || '',
                universityMongoId: spec?.universityId?._id || '',
                universityId: spec?.universityId?.universityId || '',
                university:
                    spec?.universityId?.universityName ||
                    spec?.universityName ||
                    '',
                universityLogo:
                    spec?.universityId?.universityLogo ||
                    '',

                status: spec?.status
                    ? spec.status.charAt(0).toUpperCase() + spec.status.slice(1)
                    : '',
                specializationCount: spec?.specializationCount || 0,
                completion: spec?.completionPercentage ?? null,

                createdDate: spec?.createdAt
                    ? new Date(spec.createdAt).toLocaleDateString()
                    : '',
                lastUpdate: spec?.updatedAt
                    ? new Date(spec.updatedAt).toLocaleDateString()
                    : '',

                createdBy: spec?.createdBy?.name || spec?.createdBy || '',
                reviewedBy: spec?.reviewedBy?.name || spec?.reviewedBy || '',

                comments:
                    Array.isArray(spec?.notes) && spec.notes.length > 0
                        ? spec.notes.join(', ')
                        : '',
            }));

            setRows?.(rows);
            rowCount = response?.data?.pagination?.total
            return rows;
        } else {
            setRows?.([]);
            throw new Error(`Unexpected response code: ${response.status}`);
        }
    } catch (err) {
        setRows?.([]);
        console.error('fetchSpecializationsByNameId error:', err);
        throw err;
    }finally{
        setRowCount(rowCount)
    }
};

export const fetchSpecializationNameList = async (setRows, filterData = {}, userId = true,setRowCount) => {
    try {
        const {page="",limit="",search=""} = filterData
        const query = getFilterQuery(filterData)
        const url = `${API_URL}/specialization/name${query}`
        const response = await getRequest({
            url,
            // data: filterData,
        })

        if (response.status >= 200 && response.status < 300 && response.data) {
             const pagination = response.data?.pagination
            const options = (response.data.specializations || []).map((spec, idx) => ({
                sNo: (pagination?.page-1)*pagination.limit + (idx+1),
                id: spec._id,
                specId: spec.specialisationId || '',
                name: spec.name || '',
                courseName: spec.course || '',
                tag: spec.tag || 'uncategorized',
                universityId: spec.universityId || '',
                university: spec.universityName || '',
                status: (spec.status || '').charAt(0).toUpperCase() + (spec.status || '').slice(1),
                completion: spec.completionPercentage,
                createdDate: spec.createdAt ? new Date(spec.createdAt).toLocaleDateString() : '',
                lastUpdate: spec.updatedAt ? new Date(spec.updatedAt).toLocaleDateString() : '',
                createdBy: spec.createdBy || '',
                reviewedBy: spec.reviewedBy || '',
                comments: Array.isArray(spec.notes) && spec.notes.length > 0
                    ? spec.notes.join(', ')
                    : '',
                specializationCount: spec.specializationCount || 0,
                universityCount: spec.universityCount || 0
            }))
            setRows(options)
            setRowCount && setRowCount(response.data?.pagination?.total || 0)
        } else {
            setRows([])
            throw new Error(`Unexpected response code: ${response.status}`)
        }
    } catch (error) {
        setRows([])
        console.error(error)
    }
}

export const fetchSpecializationByUniversityId = async (universityId, courseId, setCourses, filterData = {}) => {
    if (!universityId || !courseId) {
        setCourses([])
        throw new Error('University ID and Course ID are required')
    }

    try {
        const response = await getRequest({
            url: `${API_URL}/specialization/suggestion/${universityId}/${courseId}`,
            data: filterData,
        })

        if (response.status >= 200 && response.status < 300 && response.data) {
            const options = (response?.data?.specialization || response.data).map(spl => ({
                value: spl?.specializationNameId,
                label: spl?.name,
                specializationId: spl?._id
            }))
            setCourses(options)
        } else {
            setCourses([])
            throw new Error(`Unexpected response code: ${response.status}`)
        }
    } catch (error) {
        setCourses([])
        console.error(error)
    }
}

export const fetchSpecializationListByUniversityId = async ({ universityId, setRows, filterData = {}, setRowCount }) => {
    if (!universityId) {
        setRows([])
        throw new Error('University ID is required')
    }
    let list = []
    let rowCount = 0

    try {
         const query = getFilterQuery(filterData)
        const response = await getRequest({
            url: `${API_URL}/specialization/list/university/${universityId}${query}`,
            data: filterData,
        })

        if (
            response.data?.success && response.data &&
            Array.isArray(response.data.data)
        ) {
            const pagination = response.data?.pagination
            list = response.data.data.map((course,idx) => ({
                sNo: (pagination?.page-1)*pagination.limit + (idx+1),
                id: course._id,
                specialisationId: course.specialisationId,
                course: course?.courseNameId?.name,
                university: course.universityId?.universityName,
                universityLogo: course.universityId?.universityLogo,
                universityId: course.universityId?.universityId,
                specialization: course?.specializationNameId?.name,
                status: getStatusLabel(course?.status),
                createdDate: getDateFormat(course.createdAt),
                lastUpdate: getDateFormat(course.updatedAt),
                completion: course.completionPercentage || 0,
                createdBy: course?.createdBy?.fullName || '',
            }))

            rowCount = response?.data?.pagination?.total
        } else {
            throw new Error(`Unexpected response code: ${response.status}`)
        }
    } catch (error) {
        console.error(error)
        throw new Error(error?.response?.data?.message || `Something went wrong`)
    } finally {
        setRowCount && setRowCount(rowCount)
        setRows && setRows(list)
    }
}

export const fetchSpecializationListByCaseNameId = async ({courseNameId, setRows,filterData={},setRowCount}) => {
    let list = []
    let rowCount =0
    try {
      if (!courseNameId) {
        throw new Error('Course ID is required')
      }
    const query = getFilterQuery(filterData)
    const response = await getRequest({
      url: `${API_URL}/specialization/list/course/${courseNameId}${query}`,
    })

    if (response.data &&
      Array.isArray(response.data.data)
    ) {
      const pagination = response.data?.pagination
      list = response.data?.data?.map((item,idx) => ({
         sNo: (pagination?.page-1)*pagination.limit + (idx+1),
        id: item?._id,
        specialisationId: item?.specialisationId,
        course: item?.courseNameId?.name,
        specialization: item?.specializationNameId?.name,
        university: item?.universityId?.universityName,
        universityLogo: item?.universityId?.universityLogo,
        universityId: item?.universityId?.universityId,
        status: getStatusLabel(item?.status || ''),
        createdDate: item?.createdAt ? new Date(item?.createdAt).toLocaleDateString() : '',
        lastUpdate: item?.updatedAt ? new Date(item?.updatedAt).toLocaleDateString() : '',
        completion: item?.completionPercentage || 0,
        createdBy: item?.createdBy || '',
        reviewedBy: item?.reviewedBy || '',
      }))

      rowCount = response?.data?.pagination?.total
    } else {
      throw new Error(`Unexpected response code: ${response.status}`)
    }
  } catch (error) {
      console.error(error)
      throw new Error(error?.response?.data?.message || "Something went wrong")
  }finally{
    setRows(list)
    setRowCount(rowCount)
  }
}

export const fetchSpecializationsNameSuggestion = async (setSpecializations,filterData={}) => {
    try {
         const query = getFilterQuery(filterData)
        const response = await getRequest({
            url: `${API_URL}/specialization/suggestion${query}`,
        })

        if (response.status >= 200 && response.status < 300 && response.data) {
            const options = response.data?.data?.map(spec => ({
                value: spec._id,
                label: spec?.name
            }))
            setSpecializations(options)
        } else {
            setSpecializations([])
            throw new Error(`Unexpected response code: ${response.status}`)
        }
    } catch (error) {
        setSpecializations([])
        console.error(error)
    }
}